import { parseEmsDate, parseEmsTimeSpan } from "../Logic/emsDates";
import { TenantInFlightCounts, TenantMailProcessingRow } from "./CoreAPI";
import { TenantStanding, tenantStanding } from "./tenantHealth";

/**
 * A total, plus how many of the input values actually contributed.
 *
 * `total` is undefined when nothing was measured — not 0. Callers render that
 * as "—". The `measured` count lets the UI state its own coverage rather than
 * presenting a partial sum as complete.
 */
export interface MeasuredTotal {
    total?: number;
    measured: number;
}

/**
 * Sums the values that are present, ignoring those that are absent.
 *
 * Absent means the collection could not measure it; zero means it measured
 * zero. Coercing one to the other turns a tenant nobody can reach into a
 * healthy idle one, which is the exact failure this feature exists to prevent.
 */
export function sumMeasured(values: Array<number | undefined>): MeasuredTotal {
    let total = 0;
    let measured = 0;

    for (const value of values) {
        if (value !== undefined) {
            total += value;
            measured += 1;
        }
    }

    return measured === 0 ? { total: undefined, measured: 0 } : { total, measured };
}

/**
 * Errored, but may still resolve itself without a human. Operator reading:
 * watch, meaning less urgent than Given up — NOT "safe to ignore".
 *
 * The evidence is asymmetric and worth knowing. For OUTBOUND, contract §3.4
 * is explicit: an already-built email whose send failed is owned by an
 * automatic retry loop and "nothing is wrong". For INBOUND the contract says
 * no such thing — recovery there is the manual Reprocess action — so grouping
 * inbound failures into the same column is an assumption by symmetry, not
 * something the contract confirms.
 *
 * Within outbound `Failed` the two cases are also mixed: a build failure needs
 * an operator, while a send failure does not. The Core aggregate cannot
 * separate them, and the drill-down that could is out of scope.
 */
export function rowFailed(row: TenantMailProcessingRow): number | undefined {
    return sumMeasured([row.terminal?.inboundFailed, row.terminal?.outboundFailed]).total;
}

/**
 * The pipeline has stopped and nothing moves without intervention. Merges
 * dead-lettered in both directions with outbound abandoned. Operator reading:
 * act.
 *
 * Dead-lettering is removal from the queue to await manual action, so this is
 * definitionally "waiting for a human" — not an inference about retry
 * behaviour.
 *
 * Dead-lettered needs a rebuild and abandoned needs the send counter
 * re-armed, which are different remedies — but that distinction is needed on
 * the tenant mailbox view where the operator acts, not in this overview.
 */
export function rowGivenUp(row: TenantMailProcessingRow): number | undefined {
    return sumMeasured([
        row.terminal?.inboundDeadLettered,
        row.terminal?.outboundDeadLettered,
        row.terminal?.outboundAbandoned,
    ]).total;
}

/**
 * Total items sitting in the pipeline: the five queue, processing and building
 * stages.
 *
 * Deliberately excludes `mailboxes.backlog`. The design treats backlog as the
 * first stage of the inbound sequence, so the omission looks like an oversight
 * — it is not. Backlog is mail that has not been collected yet, so it is not
 * in flight, and it gets its own column as a leading indicator.
 */
export function rowInFlight(row: TenantMailProcessingRow): number | undefined {
    const inFlight = row.inFlight;
    return sumMeasured([
        inFlight?.inboundQueued?.count,
        inFlight?.inboundProcessing?.count,
        inFlight?.outboundQueued?.count,
        inFlight?.outboundBuilding?.count,
        inFlight?.outboundSendable?.count,
    ]).total;
}

/**
 * Landing view only: when this tenant was last successfully read.
 *
 * Reads `lastAttemptAt`, which carries DIFFERENT meaning on each route
 * (contract §2.2, spec §3.1). On the landing view it is the stamp of the most
 * recent tick that actually measured the tenant, with a last-seen fallback — a
 * cross-row fact. On a history row the same field is that snapshot's own
 * measurement instant, a per-tick fact.
 *
 * Phase 2 adds `historyMeasuredAt` for the other meaning. View code must call
 * one of these two rather than reading `lastAttemptAt` directly, and must not
 * share a formatter or label between the two views: doing so states the
 * landing meaning while displaying a history value.
 */
export function overviewLastReadAt(row: TenantMailProcessingRow): Date | undefined {
    return parseEmsDate(row.lastAttemptAt);
}

/**
 * A history row's own measurement instant.
 *
 * Identical in implementation to `overviewLastReadAt` today, but not identical in contract — the two are
 * only momentarily the same code. `overviewLastReadAt` has a documented future body it does not have yet:
 * `parseEmsDate(row.lastAttemptAt) ?? parseEmsDate(row.lastSuccessAt)`, the "last-seen fallback" that spec
 * §3.1 (docs/superpowers/specs/2026-08-31-cross-tenant-mail-triage-design.md) promises for the landing
 * meaning. `historyMeasuredAt` must never acquire that fallback: contract §2.2
 * (docs/api/mail-processing-contract.md) says `lastSuccessAt` is structurally absent from history rows —
 * it is landing-view-only — so reading it here would not add behaviour, it would silently do nothing while
 * looking like it does.
 *
 * That is what the duplication buys: two functions that can each grow into their own documented shape
 * without teaching the other's fallback to it by accident. This forbids more than aliasing — implementing
 * one in terms of the other, e.g. `historyMeasuredAt = row => overviewLastReadAt(row)`, reintroduces the
 * identical hazard once the fallback lands, and is forbidden for the same reason. No test enforces this: the
 * sibling test below catches an alias or a re-export, not a delegate, so this comment is the only thing
 * guarding it. See spec §3.2 (docs/superpowers/specs/2026-09-02-cross-tenant-mail-trends-design.md).
 */
export function historyMeasuredAt(row: TenantMailProcessingRow): Date | undefined {
    return parseEmsDate(row.lastAttemptAt);
}

export interface StalledQueue {
    label: string;
    count?: number;
    oldestAgeSeconds?: number;
}

// Inbound stages then outbound, matching the pipeline order the table's column
// groups use. Order is load-bearing: it determines the sequence in which
// multiple simultaneous stalls are reported. Do not sort this alphabetically.
const IN_FLIGHT_LABELS: Array<[keyof TenantInFlightCounts, string]> = [
    ["inboundQueued", "inbound queued"],
    ["inboundProcessing", "inbound processing"],
    ["outboundQueued", "outbound queued"],
    ["outboundBuilding", "outbound building"],
    ["outboundSendable", "outbound sendable"],
];

/**
 * The in-flight stages the server flagged as stalled.
 *
 * `stalled` is computed server-side against a threshold that is not in the
 * response. It is read as given and never re-derived from `oldestAt` — the
 * browser clock is the wrong reference, and a client answer would disagree
 * with the server's.
 */
export function stalledQueues(row: TenantMailProcessingRow): StalledQueue[] {
    const inFlight = row.inFlight;
    if (!inFlight) {
        return [];
    }

    const stalled: StalledQueue[] = [];
    for (const [key, label] of IN_FLIGHT_LABELS) {
        const stage = inFlight[key];
        if (stage?.stalled) {
            stalled.push({
                label,
                count: stage.count,
                oldestAgeSeconds: parseEmsTimeSpan(stage.oldestAge),
            });
        }
    }
    return stalled;
}

export interface KpiCounts {
    /**
     * Active tenants that are not fully healthy.
     *
     * Counts every tenant whose dot is not green and whose collection is not
     * switched off — collection failures, stale collections, unhealthy
     * mailboxes and stalled queues alike. Was `failing`, which counted only
     * `collectionHealth === "Failing"` and therefore reported 0 for a tenant
     * whose collector was fine and whose mailboxes were all broken.
     */
    notHealthy: number;
    stale: number;
    /**
     * Tenants whose backlog is rising.
     *
     * SPEC-2 SURFACE: computed and tested, with no consumer while the summary
     * band's Behind tile is removed and `behindKeys` is always undefined. Not
     * dead code — the deliberate seam for the deferred direction verdict.
     */
    behind?: number;
    stalledQueues: number;
    /** Absent when no tenant reported a measurable terminal block. */
    givenUp?: number;
    measuredGivenUp: number;
    /**
     * Tenants with nothing wrong, as the table's dot means it.
     *
     * `tenantStanding` green — `healthy` or `waiting`. A measured backlog is
     * mail moving, not a fault, which is why `waiting` counts here; the mailbox
     * table gives it a green dot for the same reason.
     */
    healthy: number;
    /** Disabled plus NeverCollected. Not faults. */
    inactive: number;
    total: number;
}

/**
 * The standings the table paints green.
 *
 * Kept beside the counting rather than exported from tenantHealth so there is
 * one obvious place to look when the band and the dot are suspected of
 * disagreeing — which is the bug this replaced.
 */
const HEALTHY_STANDINGS: ReadonlySet<TenantStanding> = new Set<TenantStanding>(["healthy", "waiting"]);

/**
 * Counts for the summary band.
 *
 * `behindKeys` comes from backlogTrend and is passed in rather than computed
 * here, because it needs history this function does not have. Pass
 * `undefined` when that history is not available yet (phase 1 has none) so
 * `behind` comes back `undefined` too, rather than a Set-derived zero that
 * would misreport "checked, found none" as the truth. An empty Set IS that
 * measured "none behind" answer, once phase 2 can actually supply one.
 *
 * Disabled and NeverCollected are grouped as inactive and never counted as
 * faults. Disabled in particular is a configuration statement — alarming on it
 * trains operators to ignore the band.
 */
export function kpiCounts(rows: TenantMailProcessingRow[], behindKeys: Set<string> | undefined): KpiCounts {
    const counts: KpiCounts = {
        notHealthy: 0,
        stale: 0,
        behind: undefined,
        stalledQueues: 0,
        givenUp: undefined,
        measuredGivenUp: 0,
        healthy: 0,
        inactive: 0,
        total: rows.length,
    };

    let behind = 0;

    for (const row of rows) {
        // Stale keeps its own tile as a BREAKDOWN of notHealthy, so it is
        // counted here rather than inside the bucketing below — the three
        // roster tiles must still partition the roster, and stale must not be
        // one of them.
        if (row.collectionHealth === "Stale") {
            counts.stale += 1;
        }

        switch (row.collectionHealth) {
            case "Disabled":
            case "NeverCollected":
                counts.inactive += 1;
                break;
            case "Failing":
            case "Stale":
            case "Healthy":
                // The dot decides, not the field. A tenant whose collection
                // succeeded can still be unhealthy — unreachable mailboxes, a
                // stalled queue — and the band said "healthy" over a table that
                // said otherwise until this read the same standing the dot does.
                if (HEALTHY_STANDINGS.has(tenantStanding(row))) {
                    counts.healthy += 1;
                } else {
                    counts.notHealthy += 1;
                }
                break;
            default:
                // An absent or unrecognised collectionHealth is counted in NO
                // roster tile. Landing rows always carry the field, so this is
                // not reachable through the page — but claiming health from a
                // row that reported none would be the absent-vs-zero defect
                // this feature exists to prevent. A test pins the omission.
                break;
        }

        if (stalledQueues(row).length > 0) {
            counts.stalledQueues += 1;
        }
        if (behindKeys && row.tenantKey && behindKeys.has(row.tenantKey)) {
            behind += 1;
        }
    }

    counts.behind = behindKeys ? behind : undefined;

    const givenUp = sumMeasured(rows.map(rowGivenUp));
    counts.givenUp = givenUp.total;
    counts.measuredGivenUp = givenUp.measured;

    return counts;
}
